'use client'

import { AnimatePresence, m, useReducedMotion } from 'framer-motion'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'

import { PortfolioImage } from '@/types/portfolio.types'

type DialogProps = {
  index: number
  images: PortfolioImage[]
  onClose: () => void
}

/**
 * Visionneuse des réalisations.
 *
 * Reprise pour l'accessibilité : elle n'exposait ni role="dialog", ni
 * aria-modal, ni nom accessible ; les flèches de navigation étaient des
 * boutons sans libellé ; le focus n'était pas cloisonné et n'était pas
 * restitué à l'élément déclencheur à la fermeture.
 */
export function Dialog({ index, images, onClose }: DialogProps) {
  const [current, setCurrent] = useState(index)
  const [[page, direction], setPage] = useState([index, 0])
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const shouldReduceMotion = useReducedMotion()

  useEffect(() => {
    // Mémorise le déclencheur pour lui rendre le focus à la fermeture.
    restoreFocusRef.current = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
      restoreFocusRef.current?.focus?.()
    }
  }, [])

  const paginate = useCallback(
    (newDirection: number) => {
      setPage(([p]) => [p + newDirection, newDirection])
      setCurrent((c) => {
        const next = c + newDirection
        if (next < 0) return images.length - 1
        if (next >= images.length) return 0
        return next
      })
    },
    [images.length]
  )

  const goPrev = useCallback(() => paginate(-1), [paginate])
  const goNext = useCallback(() => paginate(1), [paginate])

  // Cloisonnement du focus : Tab reste dans la visionneuse.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose()
      return
    }
    if (event.key === 'ArrowRight') {
      goNext()
      return
    }
    if (event.key === 'ArrowLeft') {
      goPrev()
      return
    }
    if (event.key !== 'Tab' || !dialogRef.current) return

    const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled])'
    )
    if (!focusables.length) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const touchStartX = useRef<number | null>(null)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const distance = touchStartX.current - e.changedTouches[0].clientX
    if (distance > 50) goNext()
    if (distance < -50) goPrev()
    touchStartX.current = null
  }

  if (!images?.length) return null
  const single = images.length <= 1

  const variants = {
    enter: (dir: number) => ({
      x: shouldReduceMotion ? 0 : dir > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (dir: number) => ({
      zIndex: 0,
      x: shouldReduceMotion ? 0 : dir < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  }

  const controlClass =
    'grid place-items-center size-11 rounded-[3px] border border-chalk/25 bg-ink/70 text-chalk backdrop-blur-md transition-colors hover:border-chalk hover:bg-chalk hover:text-ink'

  return (
    <m.div
      className="on-dark fixed inset-0 z-100 bg-ink/97 backdrop-blur-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Galerie des réalisations"
        className="absolute inset-0 flex flex-col outline-none"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className="absolute inset-x-0 top-0 z-50 flex items-center justify-between gap-4 p-4 tablet:p-6">
          {/* aria-live : le changement d'image est annoncé */}
          <p
            aria-live="polite"
            className="font-mono text-[0.6875rem] tracking-[0.16em] text-chalk"
          >
            <span className="sr-only">Image </span>
            {current + 1} / {images.length}
          </p>
          <button type="button" onClick={onClose} className={controlClass} aria-label="Fermer la galerie">
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="relative flex h-full w-full flex-1 items-center justify-center overflow-hidden">
          <AnimatePresence initial={false} custom={direction}>
            <m.div
              key={page}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'tween', ease: 'easeOut', duration: shouldReduceMotion ? 0 : 0.3 },
                opacity: { duration: 0.2 },
              }}
              className="absolute inset-0 flex h-full w-full touch-pan-y items-center justify-center"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <figure className="relative mx-auto h-full max-h-[78vh] w-full max-w-[92vw] tablet:max-w-[84vw]">
                <Image
                  src={images[current].src}
                  alt={images[current].alt}
                  fill
                  sizes="100vw"
                  className="select-none object-contain"
                  quality={90}
                  priority
                  draggable={false}
                />
              </figure>
            </m.div>
          </AnimatePresence>
        </div>

        {/* Légende + navigation : boutons réels et nommés */}
        <div className="relative z-50 flex items-center justify-between gap-6 p-4 tablet:p-6">
          <p className="max-w-[60ch] font-mono text-[0.6875rem] leading-snug text-mist">
            {images[current].alt}
          </p>

          {!single && (
            <div className="flex shrink-0 gap-2">
              <button type="button" onClick={goPrev} className={controlClass} aria-label="Image précédente">
                <ChevronLeft className="size-5" aria-hidden="true" />
              </button>
              <button type="button" onClick={goNext} className={controlClass} aria-label="Image suivante">
                <ChevronRight className="size-5" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    </m.div>
  )
}
